// apps/web/src/features/dashboards/china-requests/KpiRow.tsx (#1589, #2166)
// The 6 leadership KPI cards. The five single-value cards render via the shared
// `KpiScorecard` primitive (#2163); the two "now" snapshot cards (inWorkNow,
// overdueNow) carry NO delta by contract. The volume card is a 3-line composite
// (total + container + non-container split) that does not fit the single-value
// primitive, so it stays a bespoke card in the same grid, token-styled to match.
import type { CSSProperties } from 'react';
import type { ChinaRequestsDashboardSummary } from '@magimp/shared';
import { KpiScorecard, type KpiScorecardDelta } from '../../../ui/index.js';
import { formatNumber, formatVolume, formatWeight } from '../../../i18n/formatters.js';
import { CARD_GAP, type AppLocale, type DashboardT } from './section.js';

interface KpiRowProps {
  kpis: ChinaRequestsDashboardSummary['kpis'];
  locale: AppLocale;
  t: DashboardT;
}

interface KpiCard {
  key: string;
  label: string;
  value: string;
  delta?: KpiScorecardDelta;
}

// Build a direction-tinted delta. `suffix` is '%' for percent cards and the
// localized "pp" suffix for the conversion percentage-point delta. Rounding to
// 1 dp matches the prior bespoke card.
function buildDelta(
  value: number,
  suffix: string,
  locale: AppLocale,
  t: DashboardT,
): KpiScorecardDelta {
  const direction = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const magnitude = formatNumber(Math.abs(Math.round(value * 10) / 10), locale);
  const percentText = `${magnitude}${suffix}`;
  const ariaLabel =
    direction === 'flat'
      ? t('dashboards:chinaRequests.delta.flat')
      : direction === 'up'
        ? t('dashboards:chinaRequests.delta.increased', { value: percentText })
        : t('dashboards:chinaRequests.delta.decreased', { value: percentText });
  return { direction, percentText, ariaLabel };
}

function makeCard(key: string, label: string, value: string, delta?: KpiScorecardDelta): KpiCard {
  const card: KpiCard = { key, label, value };
  if (delta) card.delta = delta;
  return card;
}

export function KpiRow({ kpis, locale, t }: KpiRowProps) {
  const conversion = kpis.conversionPct;
  const cards: KpiCard[] = [
    makeCard(
      'newRequests',
      t('dashboards:chinaRequests.kpi.newRequests'),
      formatNumber(kpis.newRequests.value, locale),
      kpis.newRequests.deltaPct !== null
        ? buildDelta(kpis.newRequests.deltaPct, '%', locale, t)
        : undefined,
    ),
    makeCard(
      'inWorkNow',
      t('dashboards:chinaRequests.kpi.inWorkNow'),
      formatNumber(kpis.inWorkNow.value, locale),
    ),
    makeCard(
      'success',
      t('dashboards:chinaRequests.kpi.success'),
      formatNumber(kpis.success.value, locale),
      kpis.success.deltaPct !== null
        ? buildDelta(kpis.success.deltaPct, '%', locale, t)
        : undefined,
    ),
    makeCard(
      'conversion',
      t('dashboards:chinaRequests.kpi.conversion'),
      conversion.value !== null ? `${formatNumber(conversion.value, locale)}%` : '—',
      conversion.deltaPp !== null
        ? buildDelta(conversion.deltaPp, t('dashboards:chinaRequests.kpi.ppSuffix'), locale, t)
        : undefined,
    ),
    makeCard(
      'overdueNow',
      t('dashboards:chinaRequests.kpi.overdueNow'),
      formatNumber(kpis.overdueNow.value, locale),
    ),
  ];

  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: CARD_GAP,
      }}
    >
      {cards.map((card) => (
        <div key={card.key} data-testid={`china-requests-kpi-${card.key}`}>
          <KpiScorecard
            label={card.label}
            value={card.value}
            {...(card.delta ? { delta: card.delta } : {})}
          />
        </div>
      ))}
      <VolumeCard volume={kpis.volume} locale={locale} t={t} />
    </section>
  );
}

const volumeCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--s-2)',
  padding: 'var(--s-4)',
  background: 'var(--bg-1)',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--r-2)',
  boxShadow: 'var(--shadow-1)',
};

const splitLineStyle: CSSProperties = {
  fontSize: 'var(--fz-11)',
  color: 'var(--fg-3)',
  fontVariantNumeric: 'tabular-nums',
};

function VolumeCard({
  volume,
  locale,
  t,
}: {
  volume: ChinaRequestsDashboardSummary['kpis']['volume'];
  locale: AppLocale;
  t: DashboardT;
}) {
  return (
    <div data-testid="china-requests-kpi-volume" style={volumeCardStyle}>
      <span style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-2)' }}>
        {t('dashboards:chinaRequests.kpi.volume')}
      </span>
      <span
        style={{
          fontSize: 'var(--fz-16)',
          fontWeight: 600,
          color: 'var(--fg-1)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatWeight(volume.total.weightKg, locale)} ·{' '}
        {formatVolume(volume.total.volumeM3, locale)}
      </span>
      <span style={splitLineStyle}>
        {t('dashboards:chinaRequests.kpi.container')}:{' '}
        {formatWeight(volume.container.weightKg, locale)} ·{' '}
        {formatVolume(volume.container.volumeM3, locale)}
      </span>
      <span style={splitLineStyle}>
        {t('dashboards:chinaRequests.kpi.nonContainer')}:{' '}
        {formatWeight(volume.nonContainer.weightKg, locale)} ·{' '}
        {formatVolume(volume.nonContainer.volumeM3, locale)}
      </span>
    </div>
  );
}
